import React, { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, ArrowDown } from "lucide-react";
import { useLang } from "@/i18n/LanguageContext";
import { MaskLines } from "@/components/site/Reveal";
import { IMAGES } from "@/lib/images";
import { scrollToId } from "@/hooks/useLenis";

export default function Hero() {
  const { t } = useLang();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [0, 160]);
  const opacity = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

  const go = (id) => scrollToId(id);

  const stats = [t.hero.stat1, t.hero.stat2, t.hero.stat3];

  return (
    <section ref={ref} className="relative min-h-screen flex flex-col justify-center overflow-hidden">
      {/* ambient background */}
      <motion.div style={{ y }} className="absolute inset-0 z-0">
        <img src={IMAGES.heroAmbient} alt="" aria-hidden="true" className="w-full h-full object-cover" />
        <div className="absolute inset-0 aurora opacity-50 mix-blend-screen" />
        <div className="absolute inset-0 bg-gradient-to-r from-ink-bg via-ink-bg/85 to-transparent" />
        <div className="absolute inset-0 bg-gradient-to-t from-ink-bg via-transparent to-ink-bg/40" />
      </motion.div>

      <motion.div style={{ opacity }} className="relative z-10 max-w-[1400px] w-full mx-auto px-5 md:px-10 pt-28 pb-16">
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.2, duration: 1 }}
          className="font-mono text-[11px] md:text-xs uppercase tracking-[0.3em] text-cyan-brand mb-6 md:mb-8"
          data-testid="hero-overline"
        >
          {t.hero.overline}
        </motion.p>

        <h1 className="font-display font-extrabold tracking-tighter leading-[0.92] text-[3.2rem] sm:text-7xl lg:text-[7.5rem] text-white">
          <MaskLines lines={[t.hero.line1]} delay={0.15} />
          <MaskLines lines={[t.hero.line2]} lineClassName="text-gradient-cyan italic" delay={0.28} />
          <MaskLines lines={[t.hero.line3]} delay={0.4} />
        </h1>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.7, duration: 0.8 }}
          className="mt-9 flex flex-col lg:flex-row lg:items-end gap-8 lg:justify-between"
        >
          <p className="max-w-xl text-white/70 text-base md:text-lg font-light leading-relaxed">
            {t.hero.sub}
          </p>

          <div className="flex flex-wrap items-center gap-3">
            <button
              onClick={() => go("pricing")}
              data-testid="hero-cta-primary"
              className="group inline-flex items-center gap-2 rounded-full bg-cyan-brand text-ink-bg font-semibold px-6 py-3.5 hover:bg-cyan-soft transition-colors duration-200 active:scale-95"
            >
              {t.hero.ctaPrimary}
              <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform duration-200" />
            </button>
            <button
              onClick={() => go("products")}
              data-testid="hero-cta-secondary"
              className="inline-flex items-center gap-2 rounded-full border border-white/15 text-white font-medium px-6 py-3.5 hover:border-cyan-brand hover:text-cyan-brand transition-colors duration-200"
            >
              {t.hero.ctaSecondary}
            </button>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.95, duration: 0.8 }}
          className="mt-14 grid grid-cols-1 sm:grid-cols-3 gap-px rounded-2xl overflow-hidden border border-white/10 bg-white/5 backdrop-blur-xl"
        >
          {stats.map((s, i) => (
            <div key={i} className="px-6 py-5 bg-ink-surface/40" data-testid={`hero-stat-${i}`}>
              <span className="font-display text-white text-lg md:text-xl font-semibold">{s}</span>
            </div>
          ))}
        </motion.div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.3 }}
        className="absolute bottom-6 left-1/2 -translate-x-1/2 z-10 text-white/40"
        aria-hidden="true"
      >
        <ArrowDown size={20} className="animate-bounce" />
      </motion.div>
    </section>
  );
}
